import React, { FC, HTMLProps, useMemo } from 'react';
import styled from 'styled-components';
import ChevronDownIcon from '../../icons/ChevronDown';

type ContainerProps = {
  icon?: React.ReactElement;
  error?: boolean;
} & React.HTMLProps<HTMLDivElement>;
/* eslint-disable @typescript-eslint/no-unused-vars */
const ContainerComponent: FC<ContainerProps> = ({ icon, error, ...props }) => {
  return <div {...props} />;
};
const Container = styled(ContainerComponent)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  stroke: var(--gray-500);
  margin-left: 1px;

  svg {
    position: absolute;
    left: 1rem;
    top: 0;
    height: 100%;
    pointer-events: none;
  }
  svg:last-child {
    position: absolute;
    left: auto;
    right: 1rem;
    top: 0;
    height: 100%;
  }
  select {
    width: 100%;
    background: transparent;
    -webkit-appearance: none;
    border: 1px solid
      ${({ error }) => (error ? 'var(--error-600)' : 'var(--gray-300)')};
    border-radius: 8px;
    padding: 0.8rem 3rem;

    padding-left: ${({ icon }) => (icon ? '2.8rem' : '1rem')};
    box-shadow: var(--box-shadow);
    transition: 0.2s;
    color: ${({ error }) => (error ? 'var(--error-600)' : 'var(--gray-700)')};

    &:hover {
      border: 1px solid
        ${({ error }) => (error ? 'var(--error-600)' : 'var(--gray-400)')};
    }
    &:focus {
      outline: 1px solid
        ${({ error }) => (error ? 'var(--error-600)' : 'var(--gray-700)')};
    }
    &::placeholder {
      color: ${({ error }) => (error ? 'var(--error-600)' : 'var(--gray-500)')};
      opacity: 1;
    }

    &:disabled {
      border: 1px solid var(--gray-300);
    }
  }
`;

export const Label = styled(({ error, ...props }) => <p {...props} />)`
  font-size: 0.9rem;
  margin-bottom: 0.375rem;
  color: ${({ error }) => (error ? 'var(--error-600)' : 'var(--gray-700)')};
`;

type Props = {
  className?: string;
  label?: string;
  onChange?: React.ChangeEventHandler<HTMLSelectElement>;
  value: string;
  icons?: { value: string; icon: React.ReactElement }[];
  error?: boolean;
} & HTMLProps<HTMLSelectElement>;
const Select: FC<Props> = ({
  className,
  label,
  onChange,
  value,
  icons,
  children,
  readOnly,
  disabled,
  error,
  ...props
}) => {
  const icon = useMemo(() => {
    return icons?.find((icon) => icon.value === value)?.icon;
  }, [icons, value]);

  return (
    <div>
      {label && <Label error={error}>{label}</Label>}
      <Container className={className} icon={icon} error={error}>
        <select
          {...props}
          value={value}
          onChange={readOnly ? () => null : onChange}
          disabled={disabled || readOnly}
        >
          {children}
        </select>
        {icon}
        <ChevronDownIcon />
      </Container>
    </div>
  );
};

export default Select;
